// Phase 53.8 — Category Simplification + Phase 54.2 — Category Standardization.
//
// Presents a small, generic list of business categories to the operator
// while keeping ALL existing hierarchical database records untouched. Legacy
// category names ("Altın Kolye", "Gümüş Kolye", "Women's Ring", …) are
// mapped to canonical buckets at read-time via keyword matching over
// `name_tr` / `name_en` / `slug`. When the operator picks a canonical
// bucket in a filter/editor, we expand it back to the concrete list of
// legacy category IDs so the backend query hits every historical variant.
//
// Phase 54.2 — Silver + Gold inventories share the SAME canonical list
// (9 buckets). Watch inventory uses the dynamic brand catalog instead of
// generic categories, so this module exposes a scope-aware helper that
// switches between the two lists at render time.

// ── Silver + Gold canonical list (Phase 54.2) ────────────────────────────
export const CANONICAL_CATEGORIES_METAL = [
  { slug: "kolye",     label: "Kolye",     keywords: ["kolye", "necklace"] },
  { slug: "kupe",      label: "Küpe",      keywords: ["kupe", "küpe", "earring"] },
  { slug: "yuzuk",     label: "Yüzük",     keywords: ["yuzuk", "yüzük", "ring"] },
  { slug: "set",       label: "Set",       keywords: ["set", "seti", "takim", "takım"] },
  { slug: "bileklik",  label: "Bileklik",  keywords: ["bileklik", "bracelet", "bilezik", "kelepce", "kelepçe"] },
  { slug: "saat",      label: "Saat",      keywords: ["saat", "watch", "clock"] },
  { slug: "hizma",     label: "Hızma",     keywords: ["hizma", "hızma"] },
  { slug: "sahmeran",  label: "Şahmeran",  keywords: ["sahmeran", "şahmeran"] },
  { slug: "halhal",    label: "Halhal",    keywords: ["halhal", "anklet"] },
  { slug: "other",     label: "Diğer",     keywords: [] }, // Fallback bucket.
];

// Legacy alias kept for backwards compatibility with older imports.
export const CANONICAL_CATEGORIES = CANONICAL_CATEGORIES_METAL;

// Normalize helper — strips accents, lowercases, replaces non-alnum with space.
function norm(v) {
  if (v == null) return "";
  return String(v)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .toLowerCase();
}

// Resolve one legacy category row → canonical slug (metal list only).
export function resolveCanonicalSlug(cat) {
  if (!cat) return "other";
  const hay = [cat.name_tr, cat.name_en, cat.slug].map(norm).join(" | ");
  for (const c of CANONICAL_CATEGORIES_METAL) {
    if (c.slug === "other") continue;
    if (c.keywords.some((kw) => hay.includes(norm(kw)))) return c.slug;
  }
  return "other";
}

// ── Watch scope — brand list as category (Phase 54.2) ────────────────────
//
// For the Watch inventory workspace, the category dropdown should list
// active brands from the dynamic `usm_brands` collection rather than the
// generic metal buckets. Legacy watches without a brand fall into an
// "Other / Diğer" bucket so nothing gets hidden.
export function buildWatchCategoriesFromBrands(brands) {
  const active = (brands || []).filter((b) => b?.status !== "inactive");
  const list = active
    .map((b) => ({
      slug: `brand:${b.id}`,
      label: b.name,
      keywords: [b.name],
      brand_id: b.id,
    }))
    .sort((a, b) => (a.label || "").localeCompare(b.label || "", "tr"));
  list.push({ slug: "brand:none", label: "Markasız / Diğer", keywords: [], brand_id: null });
  return list;
}

// Build a map: canonical slug → array of legacy category IDs (metal scope).
export function buildSlugToIds(categories) {
  const map = {};
  CANONICAL_CATEGORIES_METAL.forEach((c) => { map[c.slug] = []; });
  (categories || []).forEach((cat) => {
    const s = resolveCanonicalSlug(cat);
    if (!map[s]) map[s] = [];
    map[s].push(cat.id);
  });
  return map;
}

// Reverse map: legacy category_id → canonical slug (metal scope only).
export function buildIdToSlug(categories) {
  const map = {};
  (categories || []).forEach((cat) => { map[cat.id] = resolveCanonicalSlug(cat); });
  return map;
}

// For the Editor: pick the best legacy category_id for a given canonical
// slug so new items route into a stable ID without creating duplicates.
export function pickBestCategoryId(canonicalSlug, categories, scopeHint) {
  if (!canonicalSlug || canonicalSlug === "other") return null;
  const canonical = CANONICAL_CATEGORIES_METAL.find((c) => c.slug === canonicalSlug);
  if (!canonical) return null;
  const candidates = (categories || []).filter((c) => resolveCanonicalSlug(c) === canonicalSlug);
  if (!candidates.length) return null;

  const targetLabels = [norm(canonical.label), ...canonical.keywords.map(norm)];
  const scored = candidates.map((c) => {
    const nt = norm(c.name_tr);
    const exact = targetLabels.includes(nt) ? 100 : 0;
    const topLevel = c.parent_id ? 0 : 20;
    let scope = 0;
    if (scopeHint) {
      const sHint = scopeHint === "silver" ? "gumus" : scopeHint === "gold" ? "altin" : scopeHint === "watch" ? "saat" : null;
      if (sHint && (norm(c.slug).includes(sHint) || norm(c.name_tr).includes(sHint))) scope = 30;
    }
    const short = 10 - Math.min(10, (c.name_tr || "").length / 4);
    return { c, score: exact + topLevel + scope + short };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored[0].c.id;
}
